import { useState } from 'react';
import { toast } from 'react-toastify';
import { fetchPropertyDeveloperAvailableTimes } from 'src/services/client';
import { scheduleWalkthrough } from 'src/helpers/eventSchedulingHelper';

/* Todo: LEGACY — Calendly backwards-compat. Self-contained suite-walkthrough scheduling, restored so
 * homeowners/occupants can still self-schedule guided walkthroughs via the property manager's
 * Calendly while Cal.com has no walkthrough equivalent. To retire: delete this hook, the schedule
 * <Overlay> in WalkThroughTable.tsx, and the openScheduleOverlay() calls in handleStatusClicked.
 * Asana (Cal.com walkthrough scheduling): TODO link. */
const TOAST_OPTIONS = {
  autoClose: 12000,
  position: 'top-center' as const,
};

interface UseCalendlyWalkthroughSchedulingArgs {
  suite: any;
  user: any;
  roleName: string;
  propertyManagerDetails: any;
  setWalkthroughStatus: (status: any) => Promise<boolean>;
}

export interface UseCalendlyWalkthroughSchedulingReturn {
  isScheduleOverlayOpen: boolean;
  selectedWalkthrough: any | null;
  availableServiceTimes: any[];
  openScheduleOverlay: (walkthrough: any) => void;
  closeScheduleOverlay: () => void;
  fetchAvailableServiceTimes: (
    id: string,
    servicePreferences?: string[]
  ) => Promise<void>;
  onWalkthroughScheduled: (data: any) => void;
}

export const useCalendlyWalkthroughScheduling = ({
  suite,
  user,
  roleName,
  propertyManagerDetails,
  setWalkthroughStatus,
}: UseCalendlyWalkthroughSchedulingArgs): UseCalendlyWalkthroughSchedulingReturn => {
  const [isScheduleOverlayOpen, setIsScheduleOverlayOpen] =
    useState<boolean>(false);
  const [selectedWalkthrough, setSelectedWalkthrough] = useState<any | null>(
    null
  );
  const [availableServiceTimes, setAvailableServiceTimes] = useState<any[]>([]);

  const openScheduleOverlay = (walkthrough: any): void => {
    setSelectedWalkthrough(walkthrough);
    setIsScheduleOverlayOpen(true);
  };

  const closeScheduleOverlay = (): void => {
    setIsScheduleOverlayOpen(false);
    setSelectedWalkthrough(null);
  };

  const fetchAvailableServiceTimes = async (
    id: string,
    servicePreferences?: string[]
  ): Promise<void> => {
    try {
      const response: any = await fetchPropertyDeveloperAvailableTimes(
        id,
        servicePreferences
      );

      if (response.ok || response.status === 200) {
        const responseJson = await response.json();
        setAvailableServiceTimes(responseJson);
        return;
      }

      toast.error(
        ' 🚫 Not able to fetch service time availability!',
        TOAST_OPTIONS
      );
    } catch (error) {
      toast.error(
        ' 🚫 Something went wrong fetching service time availability. Please try again!',
        TOAST_OPTIONS
      );
    }
  };

  const onWalkthroughScheduled = (data: any): void => {
    if (!selectedWalkthrough) {
      return;
    }

    scheduleWalkthrough(data, {
      user,
      walkthroughId: selectedWalkthrough.id,
      propertyManagerDetails,
    });

    setTimeout(() => closeScheduleOverlay(), 1000);

    const status: any = {
      walkthroughType: selectedWalkthrough.walkthroughType,
      WalkthroughId: selectedWalkthrough.id,
      SuiteId: suite.id,
      UserId: user.id,
      Role: roleName,
      Signature: '',
      Status: 'Begin',
      PropertyName: propertyManagerDetails?.propertyName,
      SuiteName: suite.number,
      PropertymanagerId: propertyManagerDetails?.propertyManager?.id || '',
      ActionUrl: `app/suites/${propertyManagerDetails?.propertyId}&${suite.id}`,
    };

    void setWalkthroughStatus(status);
  };

  return {
    isScheduleOverlayOpen,
    selectedWalkthrough,
    availableServiceTimes,
    openScheduleOverlay,
    closeScheduleOverlay,
    fetchAvailableServiceTimes,
    onWalkthroughScheduled,
  };
};
